import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ProfileHeader from './components/ProfileHeader';
import SettingsPanel from './components/SettingsPanel';
import AchievementDisplay from './components/AchievementDisplay';
import MembershipCard from './components/MembershipCard';
import UserTrustScore from './components/UserTrustScore';
import Icon from '../../components/AppIcon';


const ProfileManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [settings, setSettings] = useState({
    theme: 'dark',
    displayName: 'Alexander Thompson',
    timezone: 'America/New_York',
    currency: 'USD',
    notifications: {
      email: true,
      push: true,
      marketAlerts: true,
      auctionReminders: true,
      achievements: true,
      community: false
    },
    privacy: {
      publicProfile: true,
      showValue: false,
      allowSharing: true,
      showInLeaderboards: true
    },
    collectibleTypes: ['coins', 'stamps', 'cards', 'watches']
  });

  // Load language preference on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Show loading while auth resolves
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Build user display object from real auth data with fallbacks
  const displayUser = {
    id: user?.id,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email,
    avatar: userProfile?.avatar_url || null,
    membershipTier: userProfile?.membership_tier || userProfile?.role || 'member',
    joinDate: userProfile?.created_at ? new Date(userProfile?.created_at)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '',
    location: userProfile?.location || '',
    stats: {
      totalItems: 0,
      portfolioValue: 0,
      monthlyGrowth: 0,
      achievements: 0
    }
  };

  // Mock achievements data
  const mockAchievements = [
    {
      id: 'first_item',
      name: 'First Addition',
      description: 'Add your first collectible to the platform',
      icon: 'Plus',
      category: 'collection',
      rarity: 'common',
      unlocked: true,
      unlockedDate: 'March 15, 2023',
      recent: false,
      progress: 100
    },
    {
      id: 'collector_100',
      name: 'Serious Collector',
      description: 'Reach 100 items in your collection',
      icon: 'Package',
      category: 'collection',
      rarity: 'rare',
      unlocked: true,
      unlockedDate: 'September 8, 2024',
      recent: true,
      progress: 100
    },
    {
      id: 'value_10k',
      name: 'Portfolio Milestone',
      description: 'Achieve $10,000 in total portfolio value',
      icon: 'DollarSign',
      category: 'value',
      rarity: 'epic',
      unlocked: true,
      unlockedDate: 'August 22, 2024',
      recent: true,
      progress: 100
    },
    {
      id: 'growth_master',
      name: 'Growth Master',
      description: 'Achieve 20% monthly portfolio growth',
      icon: 'TrendingUp',
      category: 'value',
      rarity: 'legendary',
      unlocked: false,
      progress: 62,
      requirement: 'Current: 12.5% growth'
    },
    {
      id: 'community_star',
      name: 'Community Star',
      description: 'Receive 50 likes on shared collections',
      icon: 'Star',
      category: 'community',
      rarity: 'rare',
      unlocked: true,
      unlockedDate: 'September 28, 2024',
      recent: true,
      progress: 100
    },
    {
      id: 'expert_eye',
      name: 'Expert Eye',
      description: 'Successfully authenticate 10 rare items',
      icon: 'Eye',
      category: 'expertise',
      rarity: 'epic',
      unlocked: false,
      progress: 70,
      requirement: 'Authenticated: 7/10 items'
    },
    {
      id: 'market_prophet',
      name: 'Market Prophet',
      description: 'Predict 5 market trends correctly',
      icon: 'Crystal',
      category: 'expertise',
      rarity: 'legendary',
      unlocked: false,
      progress: 40,
      requirement: 'Correct predictions: 2/5'
    },
    {
      id: 'social_butterfly',
      name: 'Social Butterfly',
      description: 'Connect with 25 fellow collectors',
      icon: 'Users',
      category: 'community',
      rarity: 'rare',
      unlocked: false,
      progress: 84,
      requirement: 'Connections: 21/25'
    }
  ];

  const tabs = [
    { id: 'profile', name: 'Profile Overview', icon: 'User' },
    { id: 'settings', name: 'Settings', icon: 'Settings' },
    { id: 'achievements', name: 'Achievements', icon: 'Trophy' },
    { id: 'membership', name: 'Membership', icon: 'Crown' }
  ];

  const handleUpdateSettings = (updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const handleThemeToggle = () => {
    const newTheme = settings?.theme === 'dark' ? 'light' : 'dark';
    setSettings(prev => ({ ...prev, theme: newTheme }));
    document.documentElement?.classList?.toggle('dark', newTheme === 'dark');
  };

  const handleEditProfile = () => {
    console.log('Edit profile clicked');
  };

  const handleUpgradeMembership = (tier = 'premium') => {
    console.log('Upgrade to:', tier);
  };

  const handleManageBilling = () => {
    console.log('Manage billing clicked');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <ProfileHeader
              user={displayUser}
              onEditProfile={handleEditProfile}
              onUpgradeMembership={handleUpgradeMembership}
            />

            {/* Trust & Reputation */}
            <UserTrustScore userId={displayUser?.id} userName={displayUser?.name} />
            
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-xl p-6 premium-hover">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Activity" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Recent Activity</h3>
                    <p className="text-sm text-muted-foreground">Last 30 days</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Items Added</span>
                    <span className="font-medium text-foreground">12</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Market Updates</span>
                    <span className="font-medium text-foreground">47</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Profile Views</span>
                    <span className="font-medium text-foreground">156</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 premium-hover">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Performance</h3>
                    <p className="text-sm text-muted-foreground">This month</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Portfolio Growth</span>
                    <span className="font-medium text-success">+12.5%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Best Performer</span>
                    <span className="font-medium text-foreground">Rare Coins</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">ROI</span>
                    <span className="font-medium text-success">+18.2%</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 premium-hover">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-warning" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Community</h3>
                    <p className="text-sm text-muted-foreground">Engagement</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Connections</span>
                    <span className="font-medium text-foreground">21</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Shared Items</span>
                    <span className="font-medium text-foreground">8</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Reputation</span>
                    <span className="font-medium text-warning">Expert</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'settings':
        return (
          <SettingsPanel
            settings={settings}
            onUpdateSettings={handleUpdateSettings}
            onThemeToggle={handleThemeToggle}
          />
        );
      case 'achievements':
        return <AchievementDisplay achievements={mockAchievements} />;
      case 'membership':
        return (
          <MembershipCard
            currentTier={displayUser?.membershipTier}
            onUpgrade={handleUpgradeMembership}
            onManageBilling={handleManageBilling}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="User" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Profile Management</h1>
                <p className="text-muted-foreground">
                  Manage your account settings, preferences, and membership
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="animate-fade-in">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileManagement;